import { GuidanceStepEnum } from '@/modules/guidancestep/enums/GuidanceStepEnum';
import Lang from '/@/modules/core/app/services/Lang';
import { getters } from '/@/modules/core/app/helpers/store';
import { EstateType } from '/@/modules/estate/enums/EstateType';
import EstateJurisdictionLang from '/@/modules/predefined/services/EstateJurisdictionLang';
import { Estate } from '@/modules/estate/models/Estate';
import { CurrentEstate } from '@/modules/estate/models/CurrentEstate';
import { GuidanceStepStageKey } from '@/modules/guidancestep/enums/GuidanceStepStageKey';

export class GuidanceStepStage extends GuidanceStepEnum {
  key!: GuidanceStepStageKey;
  value!: string;
  active?: boolean;
  description!: string;
  getValue?: () => string;
  getDisplayName?: () => string;
  isOneStepStage?: boolean;
  hideSummary?: boolean;

  static CE_NOTIFY: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_NOTIFY,
    get value(): string {
      return Lang.t('guidancestep.identify-deceased-family');
    },
    active: true,
    get description(): string {
      return Lang.t('guidancestep.gather-information-estate');
    }
  };

  static CE_NOTIFY_FINANCIAL_INSTITUTIONS: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_NOTIFY_FINANCIAL_INSTITUTIONS,
    get value(): string {
      return Lang.t('guidancestep.notify-financial-institutions');
    },
    active: true,
    get description(): string {
      return Lang.t('guidancestep.request-information-financial');
    }
  };

  static CE_NOTIFY_GOVERNMENT_CREDIT_AGENCIES: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_NOTIFY_GOVERNMENT_CREDIT_AGENCIES,
    get value(): string {
      return Lang.t('guidancestep.notify-government-prevent-identity-theft');
    },
    active: true,
    get description(): string {
      return Lang.t('guidancestep.notify-third-parties');
    }
  };

  static CE_APPLY_FOR_BENEFITS: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_APPLY_FOR_BENEFITS,
    get value(): string {
      return Lang.t('guidancestep.apply-for-benefits');
    },
    active: true,
    get description(): string {
      return Lang.t('guidancestep.claim-benefits-from');
    }
  };

  static CE_PATH_AHEAD: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_PATH_AHEAD,
    get value(): string {
      return Lang.t('guidancestep.learn-assets-notify');
    },
    active: true,
    get description(): string {
      return Lang.t('guidancestep.describe-assets-how');
    }
  };

  static CE_SECURE: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_SECURE,
    get value(): string {
      return Lang.t('guidancestep.secure-hard-assets');
    },
    active: true,
    get description(): string {
      return Lang.t('guidancestep.take-secure-paperwork');
    }
  };

  static CE_INVENTORY: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_INVENTORY,
    get value(): string {
      return Lang.t('guidancestep.build-inventory');
    },
    get description(): string {
      return Lang.t('guidancestep.list-part-and');
    },
    active: true
  };

  static CE_PROBATE_DETERMINATION: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_PROBATE_DETERMINATION,
    get value(): string {
      return Lang.t('guidancestep.probate-determination');
    },
    get description(): string {
      return Lang.t('guidancestep.find-out-if-estate');
    },
    active: true
  };

  static CE_DISTRIBUTION_FRAMEWORK: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_DISTRIBUTION_FRAMEWORK,
    get value(): string {
      return Lang.t('estatedistribution.ec9fb99e-6b93-4539-a6d1-83008a988f83');
    },
    get description(): string {
      return Lang.t('guidancestep.identify-inherits-based');
    },
    active: true
  };

  static CE_PROBATE: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_PROBATE,
    get value(): string {
      return Lang.t('billing.probate');
    },
    get description(): string {
      return Lang.t('guidancestep.apply-determine-as');
    },
    getValue(): string {
      const estate: Estate | undefined = getters.auth.getCurrentEstate();
      return estate && getters.predefined.isCanadianJurisdiction(estate.jurisdictionId)
        ? Lang.t('billing.probate')
        : Lang.t('guidancestep.first-petition-for');
    },
    active: true
  };

  static CE_NOTIFY_CREDITORS: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_NOTIFY_CREDITORS,
    get value(): string {
      return Lang.t('guidancestep.notify-and-agencies');
    },
    get description(): string {
      return Lang.t('guidancestep.notify-creditors-claims');
    },
    active: true
  };

  static CE_FINALIZE_INVENTORY_VALUE: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_FINALIZE_INVENTORY_VALUE,
    get value(): string {
      return Lang.t('guidancestep.finalize-inventory-value');
    },
    get description(): string {
      return Lang.t('guidancestep.appraise-confirm-final');
    },
    active: false
  };

  static CE_ASSET_LIQUIDATION: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_ASSET_LIQUIDATION,
    get value(): string {
      return Lang.t('guidancestep.asset-liquidation');
    },
    get description(): string {
      return Lang.t('guidancestep.liquidate-assets-estate');
    },
    active: true
  };

  static CE_FILE_TAXES: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_FILE_TAXES,
    get value(): string {
      return Lang.t('guidancestep.taxes-and-creditors');
    },
    get description(): string {
      return Lang.t('guidancestep.pay-remaining-and');
    },
    active: true
  };

  static CE_INTERIM_DISTRIBUTION: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_INTERIM_DISTRIBUTION,
    get value(): string {
      return Lang.t('esign.interim-distribution');
    },
    get description(): string {
      return Lang.t('guidancestep.distribute-value-to');
    },
    active: false
  };

  static CE_CLEARANCE: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_CLEARANCE,
    get value(): string {
      return Lang.t('guidancestep.get-clearance');
    },
    get description(): string {
      return Lang.t('guidancestep.apply-government-to');
    },
    active: false
  };

  static CE_FINAL_DISTRIBUTION: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_FINAL_DISTRIBUTION,
    get value(): string {
      return Lang.t('guidancestep.final-distribution');
    },
    get description(): string {
      return Lang.t('guidancestep.distribute-the-of');
    },
    active: false
  };

  static CE_PRE_PLAN: GuidanceStepStage = {
    key: GuidanceStepStageKey.CE_PRE_PLAN,
    get value(): string {
      return Lang.t('guidancestep.pre-your-estate');
    },
    get description(): string {
      return Lang.t('guidancestep.help-loved-planning');
    },
    active: false
  };

  static CV_PERSONAL_INFORMATION: GuidanceStepStage = {
    key: GuidanceStepStageKey.CV_PERSONAL_INFORMATION,
    get value(): string {
      return Lang.t('estate.personal-information');
    },
    active: true,
    get description(): string {
      return Lang.t('guidancestep.enter-statistics-marital');
    }
  };

  static CV_INVENTORY: GuidanceStepStage = {
    key: GuidanceStepStageKey.CV_INVENTORY,
    get value(): string {
      return Lang.t('guidancestep.build-inventory');
    },
    get description(): string {
      return Lang.t('guidancestep.list-part-and');
    },
    active: true,
    isOneStepStage: true,
    hideSummary: true
  };

  static CV_DEFINE_YOUR_SITUATION: GuidanceStepStage = {
    key: GuidanceStepStageKey.CV_DEFINE_YOUR_SITUATION,
    get value(): string {
      return Lang.t('guidancestep.define-your-situation');
    },
    get description(): string {
      return Lang.t('guidancestep.story-customize-plan');
    },
    active: true,
    hideSummary: true
  };

  static CV_EXECUTORSHIP: GuidanceStepStage = {
    key: GuidanceStepStageKey.CV_EXECUTORSHIP,
    get value(): string {
      return Lang.t('guidancestep.appointments');
    },
    active: true,
    get description(): string {
      return Lang.t('guidancestep.identify-executor-to', {
        executor: EstateJurisdictionLang.executor,
        guardians: EstateJurisdictionLang.guardiansLabel
      });
    }
  };

  static CV_DISTRIBUTION_FRAMEWORK: GuidanceStepStage = {
    key: GuidanceStepStageKey.CV_DISTRIBUTION_FRAMEWORK,
    get value(): string {
      return Lang.t('estatedistribution.ec9fb99e-6b93-4539-a6d1-83008a988f83');
    },
    get description(): string {
      return Lang.t('guidancestep.identify-based-will');
    },
    active: true,
    hideSummary: true
  };

  static CV_FUNERAL_PRE_ARRANGEMENTS: GuidanceStepStage = {
    key: GuidanceStepStageKey.CV_FUNERAL_PRE_ARRANGEMENTS,
    get value(): string {
      return Lang.t('asset.funeral-pre');
    },
    get description(): string {
      return Lang.t('guidancestep.let-the-your');
    },
    hideSummary: true
  };

  static CV_WILL: GuidanceStepStage = {
    key: GuidanceStepStageKey.CV_WILL,
    get description(): string {
      const currentEstate: CurrentEstate | undefined = getters.auth.getCurrentEstate();

      if (currentEstate?.isSelfServePlan) {
        return Lang.t('guidancestep.produce-final-doc');
      }

      return Lang.t('guidancestep.plan-estate-your');
    },
    get value(): string {
      const currentEstate: CurrentEstate | undefined = getters.auth.getCurrentEstate();

      if (currentEstate?.isSelfServePlan) {
        return Lang.t('guidancestep.finalize-your-will');
      }

      return Lang.t('guidancestep.will-of-attorney');
    },
    hideSummary: true
  };

  static CV_POAS: GuidanceStepStage = {
    key: GuidanceStepStageKey.CV_POAS,
    get description(): string {
      const currentEstate: CurrentEstate | undefined = getters.auth.getCurrentEstate();

      return currentEstate?.isInQuebecJurisdiction
        ? Lang.t('guidancestep.stage-poa-quebec-desc')
        : Lang.t('guidancestep.stage-poa-roc-desc');
    },
    get value(): string {
      const currentEstate: CurrentEstate | undefined = getters.auth.getCurrentEstate();

      return currentEstate?.isInQuebecJurisdiction
        ? Lang.t('guidancestep.stage-poa-quebec-title')
        : Lang.t('guidancestep.stage-poa-roc-title');
    },
    hideSummary: true
  };

  static CV_TAXES_AND_OPTIMIZATIONS: GuidanceStepStage = {
    key: GuidanceStepStageKey.CV_TAXES_AND_OPTIMIZATIONS,
    get value(): string {
      return Lang.t('guidancestep.taxes-and-optimizations');
    },
    get description(): string {
      return '';
    }
  };

  static CV_EXECUTOR_PREP: GuidanceStepStage = {
    key: GuidanceStepStageKey.CV_EXECUTOR_PREP,
    get value(): string {
      return Lang.t('guidancestep.executor-prep');
    },
    get description(): string {
      return '';
    }
  };

  static CW_PERSONAL_INFORMATION: GuidanceStepStage = {
    key: GuidanceStepStageKey.CW_PERSONAL_INFORMATION,
    get value(): string {
      return Lang.t('estate.personal-information');
    },
    get description(): string {
      return Lang.t('guidancestep.enter-information-and');
    },
    active: true
  };

  static CW_EXECUTORSHIP: GuidanceStepStage = {
    key: GuidanceStepStageKey.CW_EXECUTORSHIP,
    get value(): string {
      return Lang.t('guidancestep.appointments');
    },
    get description(): string {
      return Lang.t('guidancestep.identify-executor-to', {
        executor: EstateJurisdictionLang.executor,
        guardians: EstateJurisdictionLang.guardiansLabel
      });
    },
    active: true
  };

  static CW_DISTRIBUTION_FRAMEWORK: GuidanceStepStage = {
    key: GuidanceStepStageKey.CW_DISTRIBUTION_FRAMEWORK,
    get value(): string {
      return Lang.t('estatedistribution.ec9fb99e-6b93-4539-a6d1-83008a988f83');
    },
    get description(): string {
      return Lang.t('guidancestep.identify-based-will');
    },
    active: true
  };

  static CW_FUNERAL_PRE_ARRANGEMENTS: GuidanceStepStage = {
    key: GuidanceStepStageKey.CW_FUNERAL_PRE_ARRANGEMENTS,
    get value(): string {
      return Lang.t('asset.funeral-pre');
    },
    get description(): string {
      return Lang.t('guidancestep.let-the-your');
    },
    hideSummary: true
  };

  static CW_WILLS_AND_POAS: GuidanceStepStage = {
    key: GuidanceStepStageKey.CW_WILLS_AND_POAS,
    get value(): string {
      return Lang.t('guidancestep.finalize-your-will');
    },
    get description(): string {
      return Lang.t('guidancestep.produce-final-doc');
    },
    hideSummary: true
  };

  static getAllClearEstateStages(): Record<string, unknown> {
    return this.getKeysByType(EstateType.CLEAR_ESTATE.key);
  }

  static getAllClearVaultStages(): Record<string, unknown> {
    return this.getKeysByType(EstateType.CLEAR_VAULT.key);
  }

  static getAllClearWillStages(): Record<string, unknown> {
    return this.getKeysByType(EstateType.CLEAR_WILL.key);
  }

  static isStageLaterThanCurrentStage(currentStage: string, stage: string): boolean {
    const stageKeys: Array<string | number> = GuidanceStepStage.getValuesByProp('key');

    return stageKeys.indexOf(currentStage) < stageKeys.indexOf(stage);
  }
}
